import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { pharmacyApi } from "@/api/pharmacy";

export default function OTReportsPage() {
  const [from, setFrom] = useState(() => new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));

  const varianceQ = useQuery({
    queryKey: ["ot-report-variance", from, to],
    queryFn: async () => (await pharmacyApi.otVarianceReport({ from, to })).data as any,
  });
  const surgeryQ = useQuery({
    queryKey: ["ot-report-surgery", from, to],
    queryFn: async () => (await pharmacyApi.otSurgeryReport({ from, to })).data as any,
  });
  const doctorQ = useQuery({
    queryKey: ["ot-report-doctor", from, to],
    queryFn: async () => (await pharmacyApi.otDoctorReport({ from, to })).data as any,
  });

  const variance = (varianceQ.data as any)?.data ?? [];
  const surgeries = (surgeryQ.data as any)?.data ?? [];
  const doctors = (doctorQ.data as any)?.data ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>OT Reports</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">From</label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">To</label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="text-xs text-muted-foreground flex items-end">
            Reports are based on OT consumption/reconciliation entries.
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="variance">
        <TabsList>
          <TabsTrigger value="variance">Variance</TabsTrigger>
          <TabsTrigger value="surgery">Surgery-wise</TabsTrigger>
          <TabsTrigger value="doctor">Doctor-wise</TabsTrigger>
        </TabsList>

        <TabsContent value="variance">
          <Card>
            <CardHeader><CardTitle className="text-base">Variance / leakage</CardTitle></CardHeader>
            <CardContent>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left p-2">Date</th>
                      <th className="text-left p-2">Drug</th>
                      <th className="text-right p-2">Expected</th>
                      <th className="text-right p-2">Physical</th>
                      <th className="text-right p-2">Variance</th>
                      <th className="text-left p-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variance.length === 0 ? (
                      <tr><td colSpan={6} className="p-3 text-muted-foreground">No variance logs</td></tr>
                    ) : variance.map((v: any) => (
                      <tr key={v.id} className="border-t">
                        <td className="p-2">{String(v.reconciliation?.date ?? v.createdAt).slice(0, 10)}</td>
                        <td className="p-2">{v.drug?.name}</td>
                        <td className="p-2 text-right">{v.expectedQty}</td>
                        <td className="p-2 text-right">{v.physicalQty}</td>
                        <td className="p-2 text-right font-medium">{v.varianceQty}</td>
                        <td className="p-2 text-muted-foreground">{v.reason || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="surgery">
          <Card>
            <CardHeader><CardTitle className="text-base">Surgery-wise consumption</CardTitle></CardHeader>
            <CardContent>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left p-2">Date</th>
                      <th className="text-left p-2">Patient</th>
                      <th className="text-left p-2">Procedure</th>
                      <th className="text-left p-2">Surgeon</th>
                      <th className="text-right p-2">Total cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {surgeries.length === 0 ? (
                      <tr><td colSpan={5} className="p-3 text-muted-foreground">No OT consumptions</td></tr>
                    ) : surgeries.map((s: any) => (
                      <tr key={s.id} className="border-t align-top">
                        <td className="p-2">{String(s.surgeryDate).slice(0, 10)}</td>
                        <td className="p-2">{s.admission?.queue?.patient?.name}</td>
                        <td className="p-2">{s.procedureName || "-"}</td>
                        <td className="p-2">{s.surgeon?.name || "-"}</td>
                        <td className="p-2 text-right">₹{Number(s.totalCost ?? 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="doctor">
          <Card>
            <CardHeader><CardTitle className="text-base">Doctor-wise usage</CardTitle></CardHeader>
            <CardContent>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left p-2">Surgeon</th>
                      <th className="text-right p-2">Surgeries</th>
                      <th className="text-right p-2">Total cost</th>
                      <th className="text-right p-2">Avg per surgery</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doctors.length === 0 ? (
                      <tr><td colSpan={4} className="p-3 text-muted-foreground">No data</td></tr>
                    ) : doctors.map((d: any) => (
                      <tr key={d.surgeonId} className="border-t">
                        <td className="p-2">{d.surgeonName}</td>
                        <td className="p-2 text-right">{d.totalSurgeries}</td>
                        <td className="p-2 text-right">₹{Number(d.totalDrugCost ?? 0).toFixed(2)}</td>
                        <td className="p-2 text-right">₹{Number(d.avgDrugCostPerSurgery ?? 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

